import {
  Box,
  Pagination,
  PaginationItem,
  Skeleton,
  SxProps,
  Theme,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import viewpaths from "global/constants/ViewPathConstants";
import {
  convertESTtoUserLocalDateAndTime,
  getNameInitials,
} from "helpers/methods";
import { LiveRegion } from "components/live-region";
import {
  cellSx,
  columnHeaderSx,
  emptyIconSx,
  emptyIconWellSx,
  emptyStateSx,
  emptyTextSx,
  emptyTitleSx,
  paginationBarSx,
  paginationSx,
  priorityDotSx,
  prioritySx,
  rowSx,
  stackedCellPrimarySx,
  stackedCellSecondarySx,
  stackedCellSx,
  summaryLinkSx,
  ticketCardAvatarSx,
  ticketCardCategorySx,
  ticketCardDateSx,
  ticketCardFooterSx,
  ticketCardHeadSx,
  ticketCardListSx,
  ticketCardNumberSx,
  ticketCardPrioritySx,
  ticketCardReporterSx,
  ticketCardSummarySx,
  ticketCardSx,
  ticketNumberSx,
  truncatedCellSx,
  unassignedPillSx,
} from "screens/Tickets/SupportTickets.styles";
import {
  NoTicketsIcon,
  PagerNextIcon,
  PagerPrevIcon,
} from "screens/Tickets/components/TicketIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

export interface Ticket {
  id: number | string;
  name: string;
  process?: string;
  priority?: string;
  /** Comma-separated list; absent when the ticket has no assignee yet. */
  assigneeName?: string;
  createdByName?: string;
  activationDate?: string;
}

interface TicketsTableProps {
  tickets: Ticket[];
  loading: boolean;
  /** Lower-cased tab label, used by the empty state ("No open tickets"). */
  emptyLabel: string;
  /** Tab key carried into the ticket detail route. */
  activeTab: string;
  rowsPerPage: number;
  page: number;
  count: number;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}

/** How many assignees fit a cell before the rest collapse into "…". */
const VISIBLE_ASSIGNEES = 2;

const formatRaisedOn = (activationDate?: string) =>
  activationDate
    ? convertESTtoUserLocalDateAndTime(
        activationDate,
        "MMMM D, YYYY [at] h:mm A",
        "MM/DD/YYYY HH:mm:ss",
      )
    : "";

/** The card footer has room for a date only, not a timestamp. */
const formatRaisedOnShort = (activationDate?: string) =>
  activationDate
    ? convertESTtoUserLocalDateAndTime(
        activationDate,
        "DD MMM YYYY",
        "MM/DD/YYYY HH:mm:ss",
      )
    : "";

/**
 * Support tickets list exactly as specified by the Settings design: a six
 * column grid (#, summary, category, priority, assignees, raised by / on)
 * inside the tabbed card, closed by a right-aligned pager. All geometry comes
 * from `SupportTickets.tokens.ts`.
 */
const TicketsTable = ({
  tickets,
  loading,
  emptyLabel,
  activeTab,
  rowsPerPage,
  page,
  count,
  onPageChange,
}: TicketsTableProps) => {
  const pageCount = Math.ceil((count || 0) / rowsPerPage);
  // Both small references replace the six-column grid with one card per ticket.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  /**
   * Loading, "no results" and result-count changes are all silent DOM swaps for
   * a screen reader, so they are mirrored into a polite live region (4.1.3).
   */
  const statusMessage = loading
    ? "Loading tickets"
    : tickets.length === 0
      ? `No ${emptyLabel} tickets`
      : `${count} ${count === 1 ? "ticket" : "tickets"} found${
          pageCount > 1 ? `, showing page ${page} of ${pageCount}` : ""
        }`;

  const getAssignees = (ticket: Ticket) => {
    if (!ticket.assigneeName) {
      return (
        <Box component="span" role="cell" sx={unassignedPillSx}>
          Unassigned
        </Box>
      );
    }

    const assignees = ticket.assigneeName.split(",");
    const visible = assignees.slice(0, VISIBLE_ASSIGNEES).join(", ");
    const isTruncated = assignees.length > VISIBLE_ASSIGNEES;
    const full = assignees.join(", ");

    // A truncated list only exists in the tooltip, so the cell has to be
    // reachable without a pointer (2.1.1); MUI already exposes the tooltip text
    // as the cell's accessible name. Untruncated cells stay out of the tab order.
    return (
      <Tooltip placement="top" title={full} arrow describeChild>
        <Box
          component="span"
          role="cell"
          sx={isTruncated ? truncatedCellSx : cellSx}
          {...(isTruncated && { tabIndex: 0, "aria-label": full })}
        >
          {isTruncated ? `${visible}, …` : visible}
        </Box>
      </Tooltip>
    );
  };

  const getRow = (ticket: Ticket, index: number) => (
    <Box key={ticket.id} role="row" sx={rowSx(index === tickets.length - 1)}>
      <Box component="span" role="cell" sx={ticketNumberSx}>
        {ticket.id}
      </Box>

      <Box role="cell" sx={{ minWidth: 0 }}>
        <Box
          component={Link}
          to={`${viewpaths.viewTicketsViewPath}/${ticket.id}?tab=${activeTab}`}
          sx={summaryLinkSx}
        >
          {ticket.name}
        </Box>
      </Box>

      <Box component="span" role="cell" sx={cellSx}>
        {ticket.process}
      </Box>

      {ticket.priority ? (
        <Box component="span" role="cell" sx={prioritySx(ticket.priority)}>
          <Box
            component="span"
            aria-hidden
            sx={priorityDotSx(ticket.priority)}
          />
          {ticket.priority}
        </Box>
      ) : (
        <Box component="span" role="cell" sx={cellSx} />
      )}

      {getAssignees(ticket)}

      <Box role="cell" sx={stackedCellSx}>
        <Box component="span" sx={stackedCellPrimarySx}>
          {ticket.createdByName}
        </Box>
        <Box component="span" sx={stackedCellSecondarySx}>
          {formatRaisedOn(ticket.activationDate)}
        </Box>
      </Box>
    </Box>
  );

  const getCard = (ticket: Ticket) => (
    <Box
      key={ticket.id}
      component={Link}
      to={`${viewpaths.viewTicketsViewPath}/${ticket.id}?tab=${activeTab}`}
      sx={ticketCardSx}
    >
      <Box sx={ticketCardHeadSx}>
        <Box component="span" sx={ticketCardNumberSx}>
          #{ticket.id}
        </Box>
        {ticket.priority && (
          <Box component="span" sx={ticketCardPrioritySx(ticket.priority)}>
            {ticket.priority}
          </Box>
        )}
        {ticket.process && (
          <Box component="span" sx={ticketCardCategorySx}>
            {ticket.process}
          </Box>
        )}
      </Box>

      <Box component="span" sx={ticketCardSummarySx}>
        {ticket.name}
      </Box>

      <Box sx={ticketCardFooterSx}>
        <Box component="span" aria-hidden="true" sx={ticketCardAvatarSx}>
          {getNameInitials(ticket.createdByName ?? "")}
        </Box>
        <Box component="span" sx={ticketCardReporterSx}>
          {ticket.createdByName}
        </Box>
        <Box component="span" sx={ticketCardDateSx}>
          {formatRaisedOnShort(ticket.activationDate)}
        </Box>
      </Box>
    </Box>
  );

  const getLoadingCards = () =>
    [...Array(3)].map((_, index) => (
      <Box key={index} sx={ticketCardSx} aria-hidden="true">
        <Skeleton variant="text" width="45%" height={14} />
        <Skeleton variant="text" width="85%" height={18} />
        <Skeleton variant="text" width="60%" height={14} />
      </Box>
    ));

  /*
    Each placeholder borrows the type metrics of the cell it stands in for. A
    text Skeleton sizes its box from `fontSize` x line-height, so a flat
    `height={16}` made every row 47px against a real row of 62px — the table
    shrank by ~150px the moment the tickets arrived. The last column is the
    stacked "raised by / on" pair, which is what sets the row's height.
  */
  const getLoadingRows = () =>
    [...Array(rowsPerPage)].map((_, index) => (
      <Box key={index} sx={rowSx(index === rowsPerPage - 1)}>
        <Skeleton
          variant="text"
          sx={[ticketNumberSx, { width: "60%" }] as SxProps<Theme>}
        />
        <Skeleton
          variant="text"
          sx={[summaryLinkSx, { width: "70%" }] as SxProps<Theme>}
        />
        <Skeleton
          variant="text"
          sx={[cellSx, { width: "55%" }] as SxProps<Theme>}
        />
        <Skeleton
          variant="text"
          sx={[cellSx, { width: "60%" }] as SxProps<Theme>}
        />
        <Skeleton
          variant="text"
          sx={[cellSx, { width: "65%" }] as SxProps<Theme>}
        />
        <Box sx={stackedCellSx}>
          <Skeleton
            variant="text"
            sx={[stackedCellPrimarySx, { width: "70%" }] as SxProps<Theme>}
          />
          <Skeleton
            variant="text"
            sx={[stackedCellSecondarySx, { width: "85%" }] as SxProps<Theme>}
          />
        </Box>
      </Box>
    ));

  const getEmptyState = () => (
    <EmptyState
      icon="ticket"
      accent="green"
      title={`No ${emptyLabel} tickets`}
      body="Nothing needs attention. Raise a ticket with support and it shows up here."
    />
  );

  return (
    <>
      {/*
       * The list is a CSS grid rather than a <table>, so the column/row
       * relationships are only visual. The ARIA table pattern restores them
       * (1.3.1) without touching a single style. Skeleton rows are hidden from
       * assistive tech instead of being announced as empty cells; `aria-busy`
       * plus the live region below cover the loading state (4.1.3).
       */}
      {isCompact ? (
        <Box
          role="list"
          aria-label="Support tickets"
          aria-busy={loading || undefined}
          sx={ticketCardListSx}
        >
          {loading
            ? getLoadingCards()
            : tickets.map((ticket) => (
                <Box
                  role="listitem"
                  key={ticket.id}
                  sx={{ display: "contents" }}
                >
                  {getCard(ticket)}
                </Box>
              ))}
        </Box>
      ) : (
        <Box
          role="table"
          aria-label="Support tickets"
          aria-busy={loading || undefined}
          aria-colcount={6}
          // The six columns have fixed widths, and the card around them clips
          // overflow — below roughly 660px the last columns were simply cut off
          // and unreachable (WCAG 1.4.10). Scrolling the grid inside its own
          // container keeps the data reachable and stops the page itself from
          // scrolling sideways. No effect at widths that already fit.
          sx={{ overflowX: "auto" }}
        >
          <Box role="rowgroup">
            <Box role="row" sx={columnHeaderSx}>
              <Box
                component="span"
                role="columnheader"
                aria-label="Ticket number"
              >
                #
              </Box>
              <Box component="span" role="columnheader">
                SUMMARY
              </Box>
              <Box component="span" role="columnheader">
                CATEGORY
              </Box>
              <Box component="span" role="columnheader">
                PRIORITY
              </Box>
              <Box component="span" role="columnheader">
                ASSIGNEE(S)
              </Box>
              <Box component="span" role="columnheader">
                RAISED BY / ON
              </Box>
            </Box>
          </Box>

          {loading ? (
            <Box aria-hidden="true">{getLoadingRows()}</Box>
          ) : (
            tickets.length > 0 && (
              <Box role="rowgroup">{tickets.map(getRow)}</Box>
            )
          )}
        </Box>
      )}

      {!loading && tickets.length === 0 && getEmptyState()}

      {!loading && pageCount > 1 && (
        <Box sx={paginationBarSx}>
          <Pagination
            page={page}
            count={pageCount}
            siblingCount={1}
            boundaryCount={1}
            shape="rounded"
            onChange={onPageChange}
            aria-label="Tickets pagination"
            sx={paginationSx}
            renderItem={(item) => (
              <PaginationItem
                slots={{ previous: PagerPrevIcon, next: PagerNextIcon }}
                {...item}
              />
            )}
          />
        </Box>
      )}

      <LiveRegion id="tickets-status" message={statusMessage} />
    </>
  );
};

export default TicketsTable;
